/**
 * In-iframe runtime for canvas HTML blocks + the postMessage protocol it
 * speaks with the parent ("cohesive-block", v1).
 *
 * Canvas block iframes are opaque-origin (`sandbox="allow-scripts"` only), so
 * the parent can never reach their DOM. This script is injected into every
 * block srcDoc and stays inert until the parent posts a `config` message; the
 * parent authenticates replies by `event.source === iframe.contentWindow`
 * (the origin of an opaque frame is the useless string "null").
 *
 * One srcDoc serves display, interactive, and edit modes — mode flips are
 * config messages, never remounts, so in-block JS state survives them.
 */

import blockRuntimeScriptSource from "./htmlBlockRuntime.script.js?raw";

export const COHESIVE_BLOCK_MESSAGE_SOURCE = "cohesive-block";
export const BLOCK_RUNTIME_VERSION = 1;

/**
 * Island-editing rule data, defined once. The runtime script re-declares the
 * nav-key list (it cannot import — it ships verbatim inside a <script> tag);
 * a test asserts the script text contains every key so the two cannot drift.
 * Non-typed keys presentation frameworks (reveal.js, impress.js, …) bind for
 * slide nav — suppressed while a caret is inside an island so typing wins.
 * Never letters like N/P/F that frameworks also bind but users need to type.
 */
export const ISLAND_NAV_KEYS: readonly string[] = [
  " ",
  "Spacebar",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
];

/**
 * Attributes the runtime injects into island descendants for the duration of
 * an edit session, so form controls cannot steal the caret. Each is paired
 * with a `data-cohesive-was-<attr>` marker holding the author's original value
 * ("" when the attribute was absent). The runtime script re-declares this list
 * (it cannot import); a test asserts the script text carries every name.
 */
export const ISLAND_INJECTED_ATTRS: readonly string[] = ["tabindex", "type"];

/** Prefix of the marker attribute pairing each injected attribute. */
export const ISLAND_INJECTED_MARKER_PREFIX = "data-cohesive-was-";

/** Selector matching any element still carrying edit-session scaffolding. */
export const ISLAND_INJECTED_SELECTOR = ISLAND_INJECTED_ATTRS.map(
  (attr) => `[${ISLAND_INJECTED_MARKER_PREFIX}${attr}]`,
).join(",");

/**
 * Set an edit-session attribute on an island descendant, recording the
 * author's original value in the paired marker — the same-origin twin of the
 * runtime script's `injectAttr`. Used by the parse-time `editable` pass in
 * `prepareSrcDocHtml` so pane-injected scaffolding is marked exactly like
 * runtime-injected scaffolding and `readIslandInnerHtml` can strip both.
 */
export function injectIslandAttr(
  el: Element,
  attr: string,
  value: string,
): void {
  const marker = `${ISLAND_INJECTED_MARKER_PREFIX}${attr}`;
  if (!el.hasAttribute(marker)) {
    const prev = el.getAttribute(attr);
    el.setAttribute(marker, prev === null ? "" : prev);
  }
  el.setAttribute(attr, value);
}

/** Put the author's markup back on one element and drop the markers. */
export function restoreInjectedIslandAttrs(el: Element): void {
  for (const attr of ISLAND_INJECTED_ATTRS) {
    const marker = `${ISLAND_INJECTED_MARKER_PREFIX}${attr}`;
    if (!el.hasAttribute(marker)) continue;
    const prev = el.getAttribute(marker);
    if (prev === "" || prev === null) el.removeAttribute(attr);
    else el.setAttribute(attr, prev);
    el.removeAttribute(marker);
  }
}

/**
 * An island's `innerHTML` with edit-session scaffolding removed — the
 * same-origin twin of the runtime script's in-frame `islandHtml`.
 *
 * The detail pane reads island content straight off its (same-origin) iframe
 * DOM rather than through runtime messages, so it must strip exactly what the
 * runtime strips. If the two disagree, the autosave engine compares a reading
 * from one against a baseline from the other, the region never comes clean,
 * and the saver loops — the same invariant `sanitizeContent` protects.
 *
 * Cleans a clone so the live document stays in session state.
 */
export function readIslandInnerHtml(el: Element): string {
  if (!el.querySelector(ISLAND_INJECTED_SELECTOR)) return el.innerHTML;
  const clone = el.cloneNode(true) as Element;
  for (const node of Array.from(
    clone.querySelectorAll(ISLAND_INJECTED_SELECTOR),
  )) {
    restoreInjectedIslandAttrs(node);
  }
  return clone.innerHTML;
}

interface BlockMessageEnvelope {
  source: typeof COHESIVE_BLOCK_MESSAGE_SOURCE;
  v: typeof BLOCK_RUNTIME_VERSION;
}

/** Content-space coordinates (iframe CSS pixels) of the gesture that entered
 *  an edit session — the runtime places the caret in the island at this point. */
export interface BlockEntryPoint {
  x: number;
  y: number;
}

/** Parent → iframe: the only downstream message. Re-sent on every mode flip;
 *  the runtime diffs and applies (toggling island contenteditable etc.). */
export interface BlockRuntimeConfig {
  /** Edit session active: islands become contenteditable and report changes. */
  editable: boolean;
  /** Persisted presentation.interactive: content receives real clicks. */
  interactive: boolean;
  /**
   * Pre-entry affordance: the host is hovering the (shielded) block and wants
   * editable islands faintly outlined. Host-driven because the shield keeps
   * all pointer events out of the iframe, so in-frame :hover can never fire.
   */
  hint?: boolean;
  /**
   * Set on the config that starts an edit session entered by double-click:
   * the runtime focuses the island under this point and places the caret
   * there (the host cannot — the frame is opaque-origin).
   */
  focusPoint?: BlockEntryPoint | null;
  /**
   * The embedder's color scheme. The runtime applies it in place — the
   * `.dark` root class the token stylesheet keys on, and `color-scheme` —
   * so a theme flip never remounts the document.
   */
  theme?: "light" | "dark";
}

/** Parent → iframe: mode configuration, re-sent on every flip. */
export type BlockConfigMessage = BlockMessageEnvelope & {
  type: "config";
} & BlockRuntimeConfig;

/**
 * One webfont face the host provisions to a block frame: `FontFace`
 * descriptors plus the font file's bytes (see blockFonts.ts for why the
 * host, not the frame, fetches them).
 */
export interface BlockFontFace {
  family: string;
  style: "normal" | "italic";
  weight: string;
  unicodeRange: string;
  data: ArrayBuffer;
}

/**
 * Parent → iframe: the block webfonts, answering the runtime's
 * `fonts-request` (and re-sent on `ready` in case the request raced the
 * host's listener). The runtime registers them before its first paint.
 */
export type BlockFontsMessage = BlockMessageEnvelope & {
  type: "fonts";
  faces: BlockFontFace[];
};

/**
 * Parent → iframe: a click forwarded from the host shield (content-space
 * coordinates). The shield keeps every real pointer event — drag, pan,
 * marquee, wheel all stay canvas gestures — and the host relays qualifying
 * clicks here instead of handing the iframe the pointer. The runtime
 * activates whatever sits under the point.
 */
export type BlockClickMessage = BlockMessageEnvelope & {
  type: "click";
  x: number;
  y: number;
};

/**
 * Parent → iframe: the pointer is resting over this content-space point
 * (the shield holds the real pointer). The runtime classifies what is there
 * with the click classifier and answers with a hover-result.
 */
export type BlockHoverMessage = BlockMessageEnvelope & {
  type: "hover";
  x: number;
  y: number;
  /** Host hover generation. The host bumps it whenever the pointer leaves
   *  the block and ignores any hover-result carrying an older value, so an
   *  answer in flight across a leave can never reopen the shield. The
   *  runtime echoes the latest generation it has seen on every answer. */
  gen: number;
};

/** Parent → iframe: the pointer left the block; clear any hover tag. */
export type BlockHoverLeaveMessage = BlockMessageEnvelope & {
  type: "hover-leave";
};

/** What a forwarded click would do at a point — the hover vocabulary. */
export type BlockHoverKind =
  | "link"
  | "island"
  | "field"
  | "control"
  | "pointer"
  | "none";

export type ParentToBlockMessage =
  | BlockConfigMessage
  | BlockClickMessage
  | BlockHoverMessage
  | BlockHoverLeaveMessage
  | BlockFontsMessage;

export type BlockToParentMessage = BlockMessageEnvelope &
  /** `islands` counts `[data-edit-id]:not([data-no-edit])` elements in the
   *  live document — the host's only view into an opaque-origin frame, used
   *  to route entry gestures and word the hover hint. Absent on runtimes
   *  older than the field (treat as unknown, not zero). */
  (| { type: "ready"; islands?: number }
    | { type: "island-focus"; regionId: string; html: string }
    | { type: "island-input"; regionId: string; html: string }
    | { type: "island-blur"; regionId: string; html: string }
    | { type: "flush-request" }
    | { type: "exit-request"; reason: "escape" }
    | { type: "link"; href: string }
    /** Answer to a forwarded click: whether it hit an interactive element
     *  (control, link, text entry). `focus: true` marks a text-entry control
     *  that took the caret — the host then focuses the iframe element so
     *  keystrokes route into the frame instead of the canvas. */
    | { type: "click-result"; interactive: boolean; focus?: boolean }
    /** The click landed on editable text (at rest via a forwarded click, or
     *  natively in interact mode): the host should open an edit session with
     *  the caret at this content-space point. */
    | { type: "edit-request"; x: number; y: number }
    /** Answer to a forwarded hover: what a click at that point would do, so
     *  the host can set the cursor before the gesture. */
    | { type: "hover-result"; kind: BlockHoverKind; gen: number }
    /** A right-click inside the frame (the browser's own menu suppressed):
     *  the host re-raises it on the node so the canvas context menu opens
     *  as it would for a right-click on the shield. Content coordinates. */
    | { type: "context-menu"; x: number; y: number }
    /** Natural size of the document at its current viewport width — the
     *  host's only way to size a surface (the lightbox) to content it cannot
     *  measure across the opaque origin. Re-announced whenever it changes.
     *  Runtimes older than the field never emit it (the host falls back to
     *  the authored height). */
    | { type: "content-size"; width: number; height: number }
    /** The document has settled: load fired, its web fonts are in, and a
     *  frame has laid out — nothing left that reflows text. Posted once; a
     *  surface that reveals on it (the lightbox) keeps a time cap in case an
     *  older runtime never sends it or `load` stalls on a third-party fetch. */
    | { type: "settled" }
    /** Posted at boot, before anything has painted: the runtime holds the
     *  document invisible for a short beat while the host answers with the
     *  block webfonts (a `fonts` message), so the first paint is already in
     *  the final faces. */
    | { type: "fonts-request" }
  );

export function createBlockConfigMessage(
  config: BlockRuntimeConfig,
): BlockConfigMessage {
  return {
    source: COHESIVE_BLOCK_MESSAGE_SOURCE,
    v: BLOCK_RUNTIME_VERSION,
    type: "config",
    editable: config.editable,
    interactive: config.interactive,
    hint: config.hint === true,
    focusPoint: config.focusPoint ?? null,
    ...(config.theme ? { theme: config.theme } : {}),
  };
}

/** Parent → iframe: the provisioned block webfonts. */
export function createBlockFontsMessage(
  faces: readonly BlockFontFace[],
): BlockFontsMessage {
  return {
    source: COHESIVE_BLOCK_MESSAGE_SOURCE,
    v: BLOCK_RUNTIME_VERSION,
    type: "fonts",
    faces: faces.map((face) => ({ ...face })),
  };
}

/** Parent → iframe: a resting hover, in content coordinates. */
export function createBlockHoverMessage(
  point: BlockEntryPoint,
  gen: number,
): BlockHoverMessage {
  return {
    source: COHESIVE_BLOCK_MESSAGE_SOURCE,
    v: BLOCK_RUNTIME_VERSION,
    type: "hover",
    x: point.x,
    y: point.y,
    gen,
  };
}

/** Parent → iframe: the pointer left the block. */
export function createBlockHoverLeaveMessage(): BlockHoverLeaveMessage {
  return {
    source: COHESIVE_BLOCK_MESSAGE_SOURCE,
    v: BLOCK_RUNTIME_VERSION,
    type: "hover-leave",
  };
}

/** Parent → iframe: a forwarded click, in content coordinates. */
export function createBlockClickMessage(
  point: BlockEntryPoint,
): BlockClickMessage {
  return {
    source: COHESIVE_BLOCK_MESSAGE_SOURCE,
    v: BLOCK_RUNTIME_VERSION,
    type: "click",
    x: point.x,
    y: point.y,
  };
}

const ISLAND_MESSAGE_TYPES = new Set([
  "island-focus",
  "island-input",
  "island-blur",
]);

/**
 * Validate an untrusted `MessageEvent.data` as a runtime message. Returns
 * null for anything that doesn't match the envelope + per-type field shapes.
 * Callers must ALSO check `event.source` against the iframe's contentWindow —
 * every opaque-origin frame on the page shares origin "null".
 */
const BLOCK_HOVER_KINDS = new Set<BlockHoverKind>([
  "link",
  "island",
  "field",
  "control",
  "pointer",
  "none",
]);

export function parseBlockMessage(data: unknown): BlockToParentMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const msg = data as Record<string, unknown>;
  if (msg.source !== COHESIVE_BLOCK_MESSAGE_SOURCE) return null;
  if (msg.v !== BLOCK_RUNTIME_VERSION) return null;
  const type = msg.type;
  if (typeof type !== "string") return null;

  if (ISLAND_MESSAGE_TYPES.has(type)) {
    if (typeof msg.regionId !== "string" || msg.regionId.length === 0) {
      return null;
    }
    if (typeof msg.html !== "string") return null;
    return data as BlockToParentMessage;
  }
  switch (type) {
    case "ready":
      if (msg.islands !== undefined && typeof msg.islands !== "number") {
        return null;
      }
      return data as BlockToParentMessage;
    case "click-result":
      if (typeof msg.interactive !== "boolean") return null;
      if (msg.focus !== undefined && typeof msg.focus !== "boolean")
        return null;
      return data as BlockToParentMessage;
    case "edit-request":
      if (typeof msg.x !== "number" || typeof msg.y !== "number") return null;
      return data as BlockToParentMessage;
    case "context-menu":
      if (typeof msg.x !== "number" || typeof msg.y !== "number") return null;
      return data as BlockToParentMessage;
    case "content-size":
      // The host sizes a real box from these; NaN / Infinity / negatives
      // would poison layout, so they are forgeries as far as it is concerned.
      if (!Number.isFinite(msg.width) || !Number.isFinite(msg.height)) {
        return null;
      }
      if ((msg.width as number) < 0 || (msg.height as number) < 0) return null;
      return data as BlockToParentMessage;
    case "settled":
    case "fonts-request":
      return data as BlockToParentMessage;
    case "hover-result":
      if (typeof msg.gen !== "number") return null;
      return BLOCK_HOVER_KINDS.has(msg.kind as BlockHoverKind)
        ? (data as BlockToParentMessage)
        : null;
    case "flush-request":
      return data as BlockToParentMessage;
    case "exit-request":
      return msg.reason === "escape" ? (data as BlockToParentMessage) : null;
    case "link":
      return typeof msg.href === "string" && msg.href.length > 0
        ? (data as BlockToParentMessage)
        : null;
    default:
      return null;
  }
}

/**
 * The in-frame script, kept as a real JS file (sibling `.script.js`) for
 * syntax highlighting and imported as raw text via Vite `?raw`. Bundlers that
 * consume the tsc dist rely on the package build copying the asset into
 * `dist/lib` (see the build scripts). ES5-style to match the nav-guard
 * precedent; it ships inside a <script> tag in the srcDoc, so it must never
 * contain the literal "</script" sequence (test-enforced). Ports the detail
 * pane's island-edit semantics (HtmlDocument.tsx setupIframeInteractions) to
 * run inside the frame: Enter → line break, paste → plain text, island
 * control suppression, presentation-nav-key suppression while an island is
 * focused, and Cmd/Ctrl+S → flush request. Link clicks are bridged up in both
 * edit and interactive modes; in-page #anchors are handled locally.
 */
export const BLOCK_RUNTIME_SCRIPT: string = blockRuntimeScriptSource;
